import { StepType } from '@heidi/schema/ml-scribe';
import type { CreateIntegrationOnboardingSessionResponse } from '../../contracts/types';

const isPrivateUrl = (url: string): boolean => {
  try {
    const { hostname } = new URL(url);

    // Strategy: Encode ALL URLs except FQDNs (Fully Qualified Domain Names)
    // This handles random IPs (like 78.65.128.16) that don't match RFC 1918 rules

    // 1. Check for localhost/loopback
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
      return true;
    }

    // 2. Check for .local mDNS hostnames
    if (hostname.endsWith('.local')) {
      return true;
    }

    // 3. Check if hostname is an IPv4 address (any IP, not just private ranges)
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
      return true; // Encode all IP addresses
    }

    // 4. Check if hostname is an IPv6 address
    if (hostname.startsWith('[') && hostname.endsWith(']')) {
      return true; // IPv6 address in brackets
    }
    if (hostname.includes(':') && !hostname.includes('.')) {
      return true; // IPv6 address (unbracketed)
    }

    // 5. Check if it's a proper FQDN with a valid TLD
    // FQDNs should have at least one dot and a TLD with at least 2 characters
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const tld = parts[parts.length - 1];
      if (tld && tld.length >= 2 && /^[a-z]+$/i.test(tld)) {
        // Has a valid TLD (e.g., .com, .org, .net), consider it a public FQDN
        return false; // Do NOT encode
      }
    }

    // 6. Everything else (bare hostnames without TLD, etc.) should be encoded
    return true;
  } catch {
    return false; // Not a valid URL, don't encode
  }
};

const decodeBase64 = (value: string): string => {
  return atob(value.replace('base64|', ''));
};

export const transformBase64Entries = (formData: Record<string, unknown>) => {
  return Object.entries(formData).reduce(
    (acc, [key, value]) => {
      if (typeof value === 'string' && value.startsWith('base64|')) {
        acc[key] = decodeBase64(value);
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
};

export const transformLocalUrlsToBase64 = (formData: Record<string, unknown>) => {
  return Object.entries(formData).reduce(
    (acc, [key, value]) => {
      if (typeof value === 'string' && value.startsWith('http') && isPrivateUrl(value)) {
        acc[key] = `base64|${btoa(value)}`;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
};

export const transformOnboardingStepLocalUrlsToBase64 = (
  data: CreateIntegrationOnboardingSessionResponse
) => {
  return {
    ...data,
    steps: data.steps.map((step) => {
      if (step.step_type === StepType.form) {
        return {
          ...step,
          payload: {
            ...(step.payload ?? {}),
            form_data: transformLocalUrlsToBase64(
              (step.payload?.form_data ?? {}) as Record<string, unknown>
            ),
          },
        };
      } else {
        return step;
      }
    }),
  } as CreateIntegrationOnboardingSessionResponse;
};

export const transformOnboardingStepBase64Entries = (
  data: CreateIntegrationOnboardingSessionResponse
) => {
  return {
    ...data,
    steps: data.steps.map((step) => {
      if (step.step_type === StepType.form) {
        return {
          ...step,
          payload: {
            ...(step.payload ?? {}),
            form_data: transformBase64Entries(
              (step.payload?.form_data ?? {}) as Record<string, unknown>
            ),
          },
        };
      } else {
        return step;
      }
    }),
  };
};
